import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CameraButton } from "@/components/CameraScanner";
import { api, apiError } from "@/lib/api";

const EMPTY = { code: "", designation: "", barcode: "", unite: "pièce", emplacement: "", categorie: "", seuil_min: 0, stock: 0 };

function Field({ label, id, children }) {
  return <div className="space-y-1.5"><Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>;
}

export function ArticleDialog({ open, onOpenChange, article, onSaved, facets, initial }) {
  const editing = !!article;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const initialBarcode = initial?.barcode || "";

  useEffect(() => { if (open) setForm(article ? { ...EMPTY, ...article, barcode: article.barcode || "" } : { ...EMPTY, barcode: initialBarcode }); }, [open, article, initialBarcode]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { designation: form.designation, barcode: form.barcode, unite: form.unite, emplacement: form.emplacement, categorie: form.categorie, seuil_min: Number(form.seuil_min) || 0 };
    try {
      const { data } = editing
        ? await api.put(`/articles/${encodeURIComponent(article.code)}`, body)
        : await api.post("/articles", { ...body, code: form.code, stock: Number(form.stock) || 0 });
      toast.success(editing ? "Article mis à jour" : "Article créé");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="article-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{editing ? `Modifier l'article ${article.code}` : "Nouvel article"}</DialogTitle>
          <DialogDescription>{editing ? "Le stock se modifie uniquement via les mouvements." : "Le stock initial sera enregistré comme mouvement d'entrée."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Code unique" id="a-code"><Input id="a-code" required disabled={editing} value={form.code} onChange={set("code")} className="h-12 font-mono" data-testid="article-code-input" /></Field>
          <Field label="Code-barres" id="a-bc">
            <div className="flex gap-2">
              <Input id="a-bc" value={form.barcode} onChange={set("barcode")} placeholder="Scannez ou saisissez" className="h-12 font-mono" data-testid="article-barcode-input" />
              <CameraButton iconOnly onDetected={(c) => setForm((f) => ({ ...f, barcode: c }))} testid="article-barcode-camera-button" title="Scanner le code-barres" />
            </div>
          </Field>
          <div className="sm:col-span-2"><Field label="Désignation" id="a-des"><Input id="a-des" required value={form.designation} onChange={set("designation")} className="h-12" data-testid="article-designation-input" /></Field></div>
          <Field label="Unité" id="a-un"><Input id="a-un" list="unites" required value={form.unite} onChange={set("unite")} className="h-12" data-testid="article-unite-input" /></Field>
          <Field label="Catégorie" id="a-cat"><Input id="a-cat" list="categories" value={form.categorie} onChange={set("categorie")} className="h-12" data-testid="article-categorie-input" /></Field>
          <Field label="Emplacement" id="a-emp"><Input id="a-emp" list="emplacements" value={form.emplacement} onChange={set("emplacement")} className="h-12" data-testid="article-emplacement-input" /></Field>
          <Field label="Seuil minimum" id="a-seuil"><Input id="a-seuil" type="number" min="0" step="any" value={form.seuil_min} onChange={set("seuil_min")} className="h-12 font-mono" data-testid="article-seuil-input" /></Field>
          {editing ? (
            <Field label="Stock actuel" id="a-st"><Input id="a-st" disabled value={form.stock} className="h-12 font-mono" data-testid="article-stock-readonly" /></Field>
          ) : (
            <Field label="Stock initial" id="a-st"><Input id="a-st" type="number" min="0" step="any" value={form.stock} onChange={set("stock")} className="h-12 font-mono" data-testid="article-stock-input" /></Field>
          )}
          <datalist id="unites">{facets?.unites?.map((u) => <option key={u} value={u} />)}</datalist>
          <datalist id="categories">{facets?.categories?.map((u) => <option key={u} value={u} />)}</datalist>
          <datalist id="emplacements">{facets?.emplacements?.map((u) => <option key={u} value={u} />)}</datalist>
          <DialogFooter className="gap-2 sm:col-span-2">
            <Button type="button" variant="outline" className="h-12" onClick={() => onOpenChange(false)} data-testid="article-cancel-button">Annuler</Button>
            <Button type="submit" className="h-12 min-w-[140px]" disabled={saving} data-testid="article-save-button">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
